import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/Form/Input';
import { EntityEditLayout } from '../../components';
import { useSaveUser, useUser } from '../../hooks/useUsers';
import { useSuppliers } from '../../hooks/useSuppliers';
import { authService } from '../../services/auth.service';
import { getPasswordValidationError, PASSWORD_REQUIREMENTS_MESSAGE } from '@ministry-of-wine/shared';

interface Supplier {
  id: number;
  name: string;
}

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  supplierId: string;
  password: string;
  confirmPassword: string;
}

export const UserEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const suppliersParams = new URLSearchParams({ page: '1', limit: '1000' });
  const currentUser = authService.getStoredUser();
  const isSupplierAdmin = currentUser?.role === 'supplier_admin';
  const { data: currentUserDetails } = useUser(currentUser?.id ?? '', Boolean(currentUser?.id) && isSupplierAdmin);
  const { data: suppliersResponse } = useSuppliers(suppliersParams);
  const { data: userData, isLoading: isUserLoading } = useUser(id as string, isEditMode && Boolean(id));
  const saveUserMutation = useSaveUser();
  const suppliers = (suppliersResponse?.data || []) as Supplier[];
  const supplierAdminSupplierId =
    currentUser?.supplierId !== null && currentUser?.supplierId !== undefined
      ? String(currentUser.supplierId)
      : currentUserDetails?.supplierId !== null && currentUserDetails?.supplierId !== undefined
        ? String(currentUserDetails.supplierId)
        : '';
  const supplierAdminSupplierName =
    currentUser?.supplierName ||
    currentUserDetails?.supplierName ||
    suppliers.find((supplier) => String(supplier.id) === supplierAdminSupplierId)?.name ||
    '';
  
  const [formData, setFormData] = useState<UserFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    supplierId: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<Partial<UserFormData>>({});
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (isEditMode && userData) {
      setFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phone: userData.phone || '',
        role: userData.role || '',
        supplierId: userData.supplierId?.toString() || '',
        password: '',
        confirmPassword: '',
      });
    }
  }, [isEditMode, userData]);

  useEffect(() => {
    if (!isSupplierAdmin || !supplierAdminSupplierId) {
      return;
    }

    setFormData((prev) => {
      const nextRole = prev.role === 'superadmin' ? '' : prev.role;
      if (prev.supplierId === supplierAdminSupplierId && nextRole === prev.role) {
        return prev;
      }

      return {
        ...prev,
        supplierId: supplierAdminSupplierId,
        role: nextRole,
      };
    });

    setErrors((prev) => ({
      ...prev,
      supplierId: undefined,
      role: undefined,
    }));
  }, [isSupplierAdmin, supplierAdminSupplierId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (isSupplierAdmin && name === 'supplierId') {
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    setSubmitError('');
    
    // Clear error for this field
    if (errors[name as keyof UserFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }

    if (name === 'password') {
      const passwordValidationError = value ? getPasswordValidationError(value) : undefined;
      const nextPassword = value;
      const nextConfirmPassword = formData.confirmPassword;

      setErrors(prev => ({
        ...prev,
        password: passwordValidationError || undefined,
        confirmPassword:
          nextConfirmPassword && nextConfirmPassword !== nextPassword
            ? 'Passwords do not match'
            : undefined,
      }));
      return;
    }

    if (name === 'confirmPassword') {
      setErrors(prev => ({
        ...prev,
        confirmPassword: value && value !== formData.password ? 'Passwords do not match' : undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<UserFormData> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.role.trim()) {
      newErrors.role = 'Role is required';
    } else if (isSupplierAdmin && formData.role === 'superadmin') {
      newErrors.role = 'Supplier admins cannot create or update super admin users';
    }

    if (isSupplierAdmin) {
      if (!supplierAdminSupplierId) {
        newErrors.supplierId = 'Your account must be assigned to a supplier before creating users';
      } else if (formData.supplierId !== supplierAdminSupplierId) {
        newErrors.supplierId = 'Supplier admins can only assign users to their own supplier';
      }
    }

    // Password validation only for new users or if password is being changed
    if (!isEditMode || formData.password) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else {
        const passwordValidationError = getPasswordValidationError(formData.password);

        if (passwordValidationError) {
          newErrors.password = passwordValidationError;
        }
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    try {
      const payload: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        supplierId:
          isSupplierAdmin
            ? parseInt(supplierAdminSupplierId, 10)
            : formData.supplierId
              ? parseInt(formData.supplierId, 10)
              : null,
      };

      // Only include password if it's set
      if (formData.password) {
        payload.password = formData.password;
      }

      await saveUserMutation.mutateAsync({
        id: isEditMode ? (id as string) : null,
        payload,
      });

      navigate('/users');
    } catch (error: unknown) {
      console.error('Error saving user:', error);
      const message =
        typeof error === 'object' && error !== null && 'message' in error
          ? Array.isArray((error as { message?: string | string[] }).message)
            ? (error as { message?: string[] }).message?.[0]
            : (error as { message?: string }).message
          : 'Failed to save user. Please try again.';

      if (message === PASSWORD_REQUIREMENTS_MESSAGE) {
        setErrors(prev => ({ ...prev, password: message }));
        return;
      }

      setSubmitError(message || 'Failed to save user. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  return (
    <EntityEditLayout
      backTo="/users"
      backLabel="Back to Users"
      title={isEditMode ? 'Edit User' : 'Create New User'}
      isLoading={isUserLoading}
    >
        <form onSubmit={handleSubmit}>
          {submitError && (
            <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg text-error-700 text-sm">
              {submitError}
            </div>
          )}
          {/* Basic Information Card */}
          <div className="mb-6">
            <div style={{
              display: 'flex',
              width: '733px',
              height: '371px',
              flexDirection: 'column',
              alignItems: 'flex-start',
              flexShrink: 0,
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #EAECF0',
              padding: '24px'
            }}>
              <h2 style={{
                fontFamily: 'Oxygen',
                fontSize: '18px',
                fontWeight: 700,
                color: '#101828',
                marginBottom: '24px'
              }}>
                Basic Information
              </h2>

              <div className="space-y-6 w-full">
                {/* First Name */}
                <div>
                  <label htmlFor="firstName" style={{
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#344054',
                    display: 'block',
                    marginBottom: '6px'
                  }}>
                    First Name <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Enter first name"
                    error={errors.firstName}
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" style={{
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#344054',
                    display: 'block',
                    marginBottom: '6px'
                  }}>
                    Last Name <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Enter last name"
                    error={errors.lastName}
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" style={{
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#344054',
                    display: 'block',
                    marginBottom: '6px'
                  }}>
                    Email <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    error={errors.email}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contact & Role Information Card */}
          <div className="mb-6">
            <div style={{
              display: 'flex',
              width: '733px',
              height: '371px',
              flexDirection: 'column',
              alignItems: 'flex-start',
              flexShrink: 0,
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #EAECF0',
              padding: '24px'
            }}>
              <h2 style={{
                fontFamily: 'Oxygen',
                fontSize: '18px',
                fontWeight: 700,
                color: '#101828',
                marginBottom: '24px'
              }}>
                Contact & Role Information
              </h2>

              <div className="space-y-6 w-full">
                {/* Phone */}
                <div>
                  <label htmlFor="phone" style={{
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#344054',
                    display: 'block',
                    marginBottom: '6px'
                  }}>
                    Phone
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    error={errors.phone}
                  />
                </div>

                {/* Role */}
                <div>
                  <label htmlFor="role" style={{
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#344054',
                    display: 'block',
                    marginBottom: '6px'
                  }}>
                    Role <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      height: '44px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: errors.role ? '1px solid #DC2626' : '1px solid #D0D5DD',
                      fontFamily: 'Oxygen',
                      fontSize: '16px',
                      color: '#101828',
                      backgroundColor: 'white',
                    }}
                  >
                    <option value="">Select a role</option>
                    {!isSupplierAdmin && <option value="superadmin">Super Admin</option>}
                    <option value="supplier_admin">Supplier Admin</option>
                    <option value="supplier_user">Supplier User</option>
                  </select>
                  {errors.role && (
                    <p style={{ color: '#DC2626', fontSize: '14px', marginTop: '6px' }}>
                      {errors.role}
                    </p>
                  )}
                </div>

                {/* Supplier */}
                <div>
                  <label htmlFor="supplierId" style={{
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#344054',
                    display: 'block',
                    marginBottom: '6px'
                  }}>
                    Supplier
                  </label>
                  {isSupplierAdmin ? (
                    <Input
                      id="supplierId"
                      name="supplierId"
                      value={supplierAdminSupplierName}
                      readOnly
                      placeholder="Loading supplier..."
                      error={errors.supplierId}
                    />
                  ) : (
                    <select
                      id="supplierId"
                      name="supplierId"
                      value={formData.supplierId}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        height: '44px',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: errors.supplierId ? '1px solid #DC2626' : '1px solid #D0D5DD',
                        fontFamily: 'Oxygen',
                        fontSize: '16px',
                        color: '#101828',
                        backgroundColor: 'white',
                      }}
                    >
                      <option value="">No supplier</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {!isSupplierAdmin && errors.supplierId && (
                    <p style={{ color: '#DC2626', fontSize: '14px', marginTop: '6px' }}>
                      {errors.supplierId}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Password Card - Only show for new users or when updating password */}
          {(!isEditMode || formData.password || formData.confirmPassword) && (
            <div className="mb-6">
              <div style={{
                display: 'flex',
                width: '733px',
                minHeight: '250px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                flexShrink: 0,
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #EAECF0',
                padding: '24px'
              }}>
                <h2 style={{
                  fontFamily: 'Oxygen',
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#101828',
                  marginBottom: '24px'
                }}>
                  {isEditMode ? 'Change Password' : 'Password'}
                </h2>

                <div className="space-y-6 w-full">
                  {/* Password */}
                  <div>
                    <label htmlFor="password" style={{
                      fontFamily: 'Oxygen',
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#344054',
                      display: 'block',
                      marginBottom: '6px'
                    }}>
                      Password {!isEditMode && <span style={{ color: '#DC2626' }}>*</span>}
                    </label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter password (min 8 characters)"
                      error={errors.password}
                      hint={PASSWORD_REQUIREMENTS_MESSAGE}
                    />
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" style={{
                      fontFamily: 'Oxygen',
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#344054',
                      display: 'block',
                      marginBottom: '6px'
                    }}>
                      Confirm Password {!isEditMode && <span style={{ color: '#DC2626' }}>*</span>}
                    </label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Re-enter password"
                      error={errors.confirmPassword}
                    />
                    {!errors.confirmPassword && formData.confirmPassword && formData.password === formData.confirmPassword && (
                      <p style={{ color: '#10B981', fontSize: '12px', marginTop: '6px' }}>
                        ✓ Passwords match
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {isEditMode && !formData.password && !formData.confirmPassword && (
            <div className="mb-6">
              <Button
                type="button"
                variant="neutral"
                onClick={() => setFormData(prev => ({ ...prev, password: ' ', confirmPassword: ' ' }))}
              >
                Change Password
              </Button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-8 max-w-[733px]">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancel}
              disabled={saveUserMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saveUserMutation.isPending}>
              {saveUserMutation.isPending ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create User'}
            </Button>
          </div>
        </form>
    </EntityEditLayout>
  );
};

export default UserEditPage;
